import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Bookmark,
  BookmarkCheck,
  StickyNote,
  GraduationCap,
  ExternalLink,
  Trash2,
  Plus,
  Pencil,
  ChevronUp,
  ChevronDown,
  Highlighter,
  Check,
  ListTree,
  FileUp,
  X,
} from "lucide-react";
import Markdown from "@/components/Markdown";
import RichTextEditor from "@/components/RichTextEditor";
import LibraryEditor from "@/features/library/LibraryEditor";
import ImportWizard from "@/features/library/ImportWizard";
import type { SubSession } from "@/content/library";
import { useLibraryStore } from "@/store/libraryStore";
import { useCategoryStore } from "@/store/categoryStore";
import { useLibraryContentStore } from "@/store/libraryContentStore";

function scrollToSub(id: string) {
  document.getElementById(`sub-${id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

interface Selection {
  subId: string;
  text: string;
  occurrence: number;
  x: number;
  y: number;
}

const HL_SWATCHES: { key: string; className: string }[] = [
  { key: "yellow", className: "bg-amber-300" },
  { key: "green", className: "bg-emerald-300" },
  { key: "blue", className: "bg-sky-300" },
  { key: "pink", className: "bg-pink-300" },
  { key: "purple", className: "bg-violet-300" },
];
const HL_DOT: Record<string, string> = Object.fromEntries(HL_SWATCHES.map((s) => [s.key, s.className]));

function countOccurrences(hay: string, needle: string): number {
  if (!needle) return 0;
  let c = 0;
  let i = hay.indexOf(needle);
  while (i !== -1) {
    c++;
    i = hay.indexOf(needle, i + needle.length);
  }
  return c;
}

export default function Library() {
  const navigate = useNavigate();
  const setOnly = useCategoryStore((s) => s.setOnly);

  const subsOf = useLibraryContentStore((s) => s.subsOf);
  const subSessions = useLibraryContentStore((s) => s.subSessions);
  const mainSessions = useLibraryContentStore((s) => s.mainSessions);
  const addMain = useLibraryContentStore((s) => s.addMain);
  const renameMain = useLibraryContentStore((s) => s.renameMain);
  const removeMain = useLibraryContentStore((s) => s.removeMain);
  const moveMain = useLibraryContentStore((s) => s.moveMain);
  const removeSub = useLibraryContentStore((s) => s.removeSub);
  const moveSub = useLibraryContentStore((s) => s.moveSub);

  const bookmarks = useLibraryStore((s) => s.bookmarks);
  const addHighlight = useLibraryStore((s) => s.addHighlight);

  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importedNote, setImportedNote] = useState("");
  const [editor, setEditor] = useState<{ open: boolean; sub: SubSession | null; mainId?: string }>({ open: false, sub: null });
  const [renamingMain, setRenamingMain] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [sel, setSel] = useState<Selection | null>(null);
  const [noteText, setNoteText] = useState("");
  const [noting, setNoting] = useState(false);
  const [noteColor, setNoteColor] = useState("yellow");

  const readingRef = useRef<HTMLDivElement>(null);

  const mains = [...mainSessions].sort((a, b) => a.order - b.order);
  const q = query.trim().toLowerCase();

  const matchSub = (s: SubSession) =>
    !q || `${s.title} ${s.body} ${s.tags.join(" ")}`.toLowerCase().includes(q);

  const savedSubs = bookmarks
    .map((id) => subSessions.find((s) => s.id === id))
    .filter((s): s is SubSession => Boolean(s));

  // ---- selection → highlight popup ----
  function onReadingMouseUp() {
    const selection = window.getSelection();
    const text = selection?.toString().trim() ?? "";
    if (!selection || !text || selection.rangeCount === 0) {
      setSel(null);
      return;
    }
    const node: Node | null = selection.anchorNode;
    const el = node instanceof Element ? node : node?.parentElement ?? null;
    // The body element scopes occurrence-counting to exactly what Markdown renders.
    const host = el?.closest("[data-hl-id]") as HTMLElement | null;
    if (!host) {
      setSel(null);
      return;
    }
    const range = selection.getRangeAt(0);
    // Count how many times `text` appears in the body before this selection.
    const pre = range.cloneRange();
    pre.selectNodeContents(host);
    pre.setEnd(range.startContainer, range.startOffset);
    const occurrence = countOccurrences(pre.toString(), text);
    const rect = range.getBoundingClientRect();
    setSel({ subId: host.dataset.hlId!, text, occurrence, x: rect.left + rect.width / 2, y: rect.top });
    setNoting(false);
    setNoteText("");
  }

  function commitHighlight(color: string, withNote: boolean) {
    if (!sel) return;
    if (withNote && !noting) {
      setNoteColor(color);
      setNoting(true);
      return;
    }
    addHighlight(sel.subId, sel.text, withNote ? noteText.trim() : "", color, sel.occurrence);
    window.getSelection()?.removeAllRanges();
    setSel(null);
    setNoting(false);
    setNoteText("");
  }

  function practice(disciplineId: string) {
    setOnly(disciplineId);
    navigate("/challenge");
  }

  // Jump to a bookmarked section. Clear any active search first, otherwise the
  // target may be filtered out of the reading column and the scroll silently no-ops.
  function openBookmark(id: string) {
    if (q) {
      setQuery("");
      requestAnimationFrame(() => scrollToSub(id));
    } else {
      scrollToSub(id);
    }
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="rounded-2xl border border-border bg-gradient-to-br from-surface to-surface-2 p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-display text-2xl font-bold text-text">The Game Design Compendium</h2>
            <p className="mt-1 text-sm text-muted">One continuous book — organized into your own sessions. Select any text to highlight it.</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setEditing((v) => !v)}
              className={[
                "inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors",
                editing ? "border-primary bg-primary/10 text-text" : "border-border bg-surface text-muted hover:text-text",
              ].join(" ")}
            >
              <ListTree className="h-4 w-4" /> {editing ? "Done editing" : "Edit structure"}
            </button>
            <button
              onClick={() => setImporting(true)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-1.5 text-sm font-medium text-muted transition-colors hover:text-text"
            >
              <FileUp className="h-4 w-4" /> Import
            </button>
            <button
              onClick={() => setEditor({ open: true, sub: null, mainId: mains[0]?.id })}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-primary-fg shadow-glow transition-transform hover:scale-[1.03]"
            >
              <Plus className="h-4 w-4" /> Add
            </button>
          </div>
        </div>
        {importedNote && (
          <div className="mt-3 flex items-center gap-2 rounded-lg border border-emerald-400/30 bg-emerald-400/10 px-3 py-2 text-sm text-emerald-300">
            <Check className="h-4 w-4" /> {importedNote}
          </div>
        )}
      </div>

      <div className="flex flex-col gap-6 lg:flex-row">
        {/* Sidebar TOC */}
        <aside className="lg:w-72 lg:shrink-0">
          <div className="lg:sticky lg:top-4 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto lg:pr-1">
            <div className="relative mb-3">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search the book…"
                className="w-full rounded-lg border border-border bg-surface py-2 pl-9 pr-3 text-sm text-text placeholder:text-muted focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
              />
            </div>

            {savedSubs.length > 0 && (
              <div className="mb-4">
                <div className="mb-1.5 flex items-center gap-1.5 px-1 text-[11px] font-semibold uppercase tracking-wider text-muted">
                  <Bookmark className="h-3.5 w-3.5" /> Bookmarks
                </div>
                <ul>
                  {savedSubs.map((s) => (
                    <li key={s.id}>
                      <button onClick={() => openBookmark(s.id)} className="w-full truncate rounded-md px-2 py-1 text-left text-sm text-muted hover:bg-surface-2 hover:text-text">{s.title}</button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <nav className="space-y-3">
              {mains.map((m) => {
                const subs = subsOf(m.id).filter(matchSub);
                if (subs.length === 0 && q) return null;
                return (
                  <div key={m.id}>
                    <div className="px-1 text-[11px] font-semibold uppercase tracking-wider text-muted">{m.title}</div>
                    <ul className="mt-1">
                      {subs.map((s) => (
                        <li key={s.id}>
                          <button onClick={() => scrollToSub(s.id)} className="block w-full truncate rounded-md px-2 py-1 text-left text-sm text-muted transition-colors hover:bg-surface-2 hover:text-text">
                            {s.title}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </nav>

            {editing && (
              <button
                onClick={() => addMain("New session")}
                className="mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-border px-3 py-2 text-sm text-muted hover:border-primary/50 hover:text-text"
              >
                <Plus className="h-4 w-4" /> Add main session
              </button>
            )}
          </div>
        </aside>

        {/* Reading column — the continuous book */}
        <section ref={readingRef} onMouseUp={onReadingMouseUp} className="min-w-0 flex-1">
          <div className="mx-auto max-w-2xl">
            {mains.map((m) => {
              const subs = subsOf(m.id).filter(matchSub);
              if (subs.length === 0 && q) return null;
              const renaming = renamingMain === m.id;
              return (
                <div key={m.id} className="mb-12">
                  {/* Main session (Part) heading */}
                  <div className="mb-6 border-b border-border pb-2">
                    <div className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Session</div>
                    {renaming ? (
                      <div className="mt-1 flex items-center gap-2">
                        <input
                          autoFocus
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") { renameMain(m.id, renameValue); setRenamingMain(null); }
                            if (e.key === "Escape") setRenamingMain(null);
                          }}
                          className="flex-1 rounded-lg border border-border bg-bg px-3 py-1.5 font-display text-2xl font-bold text-text focus:border-primary focus:outline-none"
                        />
                        <button onClick={() => { renameMain(m.id, renameValue); setRenamingMain(null); }} className="grid h-8 w-8 place-items-center rounded-lg bg-primary text-primary-fg"><Check className="h-4 w-4" /></button>
                        <button onClick={() => setRenamingMain(null)} className="grid h-8 w-8 place-items-center rounded-lg border border-border text-muted"><X className="h-4 w-4" /></button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <h2 className="font-display text-3xl font-bold text-text">{m.title}</h2>
                        {editing && (
                          <div className="ml-auto flex items-center gap-1">
                            <IconBtn label="Rename" onClick={() => { setRenamingMain(m.id); setRenameValue(m.title); }}><Pencil className="h-4 w-4" /></IconBtn>
                            <IconBtn label="Move up" onClick={() => moveMain(m.id, -1)}><ChevronUp className="h-4 w-4" /></IconBtn>
                            <IconBtn label="Move down" onClick={() => moveMain(m.id, 1)}><ChevronDown className="h-4 w-4" /></IconBtn>
                            <IconBtn label="Add sub-session" onClick={() => setEditor({ open: true, sub: null, mainId: m.id })}><Plus className="h-4 w-4" /></IconBtn>
                            <IconBtn label="Delete session" onClick={() => { if (confirm(`Delete "${m.title}" and all its sub-sessions?`)) removeMain(m.id); }}><Trash2 className="h-4 w-4" /></IconBtn>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {subs.length === 0 && (
                    <p className="text-sm text-muted">No sub-sessions yet. {editing ? "Use + to add one." : ""}</p>
                  )}

                  {subs.map((s) => (
                    <SubArticle
                      key={s.id}
                      sub={s}
                      query={q}
                      editing={editing}
                      onEdit={() => setEditor({ open: true, sub: s })}
                      onDelete={() => { if (confirm(`Delete "${s.title}"?`)) removeSub(s.id); }}
                      onMove={(dir) => moveSub(s.id, dir)}
                      onPractice={s.disciplineId ? () => practice(s.disciplineId!) : undefined}
                    />
                  ))}
                </div>
              );
            })}
          </div>
        </section>
      </div>

      {/* Selection → highlight popup */}
      {sel && (
        <div
          style={{ position: "fixed", left: sel.x, top: sel.y - 8, transform: "translate(-50%, -100%)", zIndex: 60 }}
          onMouseDown={(e) => e.preventDefault()}
          className="rounded-lg border border-border bg-surface shadow-2xl"
        >
          {noting ? (
            <div className="flex items-center gap-1.5 p-1.5">
              <input
                autoFocus
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && commitHighlight(noteColor, true)}
                placeholder="Add a note…"
                className="w-44 rounded-md border border-border bg-bg px-2 py-1 text-sm text-text placeholder:text-muted focus:border-primary focus:outline-none"
              />
              <div className="flex items-center gap-1">
                {HL_SWATCHES.map((s) => (
                  <button
                    key={s.key}
                    onClick={() => setNoteColor(s.key)}
                    aria-label={s.key}
                    className={[s.className, "h-4 w-4 rounded-full ring-offset-1 ring-offset-surface", noteColor === s.key ? "ring-2 ring-text" : ""].join(" ")}
                  />
                ))}
              </div>
              <button onClick={() => commitHighlight(noteColor, true)} className="rounded-md bg-primary px-2 py-1 text-xs font-semibold text-primary-fg">Save</button>
            </div>
          ) : (
            <div className="flex items-center gap-1 p-1.5">
              <span className="pl-1 text-xs text-muted"><Highlighter className="h-4 w-4 text-amber-400" /></span>
              {HL_SWATCHES.map((s) => (
                <button
                  key={s.key}
                  onClick={() => commitHighlight(s.key, false)}
                  aria-label={`Highlight ${s.key}`}
                  title={`Highlight ${s.key}`}
                  className={[s.className, "h-5 w-5 rounded-full transition-transform hover:scale-110"].join(" ")}
                />
              ))}
              <button onClick={() => commitHighlight(noteColor, true)} title="Highlight with a note" className="ml-1 inline-flex items-center gap-1 border-l border-border pl-2 pr-1 text-sm font-medium text-text hover:text-primary">
                <StickyNote className="h-4 w-4 text-primary" />
              </button>
            </div>
          )}
        </div>
      )}

      {editor.open && (
        <LibraryEditor sub={editor.sub} defaultMainId={editor.mainId} onClose={() => setEditor({ open: false, sub: null })} />
      )}

      {importing && (
        <ImportWizard
          onClose={() => setImporting(false)}
          onDone={(n) => {
            setImportedNote(n > 0 ? `Added ${n} note${n === 1 ? "" : "s"} to your Library.` : "");
            if (n > 0) setTimeout(() => setImportedNote(""), 6000);
          }}
        />
      )}
    </div>
  );
}

function SubArticle({
  sub,
  query,
  editing,
  onEdit,
  onDelete,
  onMove,
  onPractice,
}: {
  sub: SubSession;
  query: string;
  editing: boolean;
  onEdit: () => void;
  onDelete: () => void;
  onMove: (dir: -1 | 1) => void;
  onPractice?: () => void;
}) {
  const isBookmarked = useLibraryStore((s) => s.isBookmarked);
  const toggleBookmark = useLibraryStore((s) => s.toggleBookmark);
  const getNote = useLibraryStore((s) => s.getNote);
  const setNote = useLibraryStore((s) => s.setNote);
  const getHighlights = useLibraryStore((s) => s.getHighlights);
  const removeHighlight = useLibraryStore((s) => s.removeHighlight);

  const [showNotes, setShowNotes] = useState(false);
  const marked = isBookmarked(sub.id);
  const highlights = getHighlights(sub.id);

  return (
    <article id={`sub-${sub.id}`} data-sub-id={sub.id} className="group scroll-mt-4 pt-2">
      {/* Heading + hover action strip */}
      <div className="flex items-start gap-2">
        <h3 className="flex-1 font-display text-xl font-semibold text-text">{sub.title}</h3>
        <div className="flex items-center gap-0.5 opacity-60 transition-opacity sm:opacity-0 sm:group-hover:opacity-100">
          <IconBtn label={marked ? "Bookmarked" : "Bookmark"} active={marked} onClick={() => toggleBookmark(sub.id)}>
            {marked ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
          </IconBtn>
          <IconBtn label="Notes" active={showNotes} onClick={() => setShowNotes((v) => !v)}><StickyNote className="h-4 w-4" /></IconBtn>
          {onPractice && <IconBtn label="Practice this" onClick={onPractice}><GraduationCap className="h-4 w-4" /></IconBtn>}
          <IconBtn label="Edit" onClick={onEdit}><Pencil className="h-4 w-4" /></IconBtn>
          {editing && (
            <>
              <IconBtn label="Move up" onClick={() => onMove(-1)}><ChevronUp className="h-4 w-4" /></IconBtn>
              <IconBtn label="Move down" onClick={() => onMove(1)}><ChevronDown className="h-4 w-4" /></IconBtn>
              <IconBtn label="Delete" onClick={onDelete}><Trash2 className="h-4 w-4" /></IconBtn>
            </>
          )}
        </div>
      </div>

      {/* Body flows directly; data-hl-id scopes highlight occurrence-counting. */}
      <div className="mt-1" data-hl-id={sub.id}>
        <Markdown source={sub.body} highlights={highlights} query={query} />
      </div>

      {/* Notes panel */}
      {showNotes && (
        <div className="mt-3">
          <div className="mb-1 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted"><StickyNote className="h-3.5 w-3.5" /> Notes</div>
          <RichTextEditor value={getNote(sub.id)} onChange={(md) => setNote(sub.id, md)} placeholder="Your notes on this section… (autosaved)" minHeight="6rem" />
        </div>
      )}

      {/* All highlights — manage & delete */}
      {highlights.length > 0 && (
        <div className="mt-3">
          <div className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted">
            <Highlighter className="h-3.5 w-3.5 text-amber-400" /> Highlights ({highlights.length})
          </div>
          <div className="space-y-1.5">
            {highlights.map((h) => (
              <div key={h.id} className="flex items-start gap-2 rounded-lg border border-border bg-surface-2/50 p-2.5 text-sm">
                <span className={[HL_DOT[h.color ?? "yellow"] ?? HL_DOT.yellow, "mt-1 h-3 w-3 shrink-0 rounded-full"].join(" ")} />
                <div className="min-w-0 flex-1">
                  <span className="italic text-muted">“{h.text}”</span>
                  {h.comment && <span className="text-text"> — {h.comment}</span>}
                </div>
                <button onClick={() => removeHighlight(sub.id, h.id)} aria-label="Remove highlight" title="Remove highlight" className="text-muted hover:text-amber-400"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sources as a quiet footnote */}
      {sub.sources.length > 0 && (
        <p className="mt-3 text-xs text-muted">
          <span className="font-semibold uppercase tracking-wide">Sources: </span>
          {sub.sources.map((s, n) => (
            <span key={n}>
              {n > 0 && "; "}
              <span className="text-text/80">{s.title}</span>
              {s.author ? ` — ${s.author}` : ""}
              {s.url && (
                <a href={s.url} target="_blank" rel="noreferrer" className="ml-1 inline-flex items-center gap-0.5 text-primary underline">link <ExternalLink className="h-3 w-3" /></a>
              )}
            </span>
          ))}
        </p>
      )}
    </article>
  );
}

function IconBtn({
  children,
  onClick,
  label,
  active = false,
}: {
  children: React.ReactNode;
  onClick: () => void;
  label: string;
  active?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      title={label}
      className={["grid h-7 w-7 place-items-center rounded-md transition-colors", active ? "text-primary" : "text-muted hover:bg-surface-2 hover:text-text"].join(" ")}
    >
      {children}
    </button>
  );
}
